'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const APP_NAME = 'glanz_crm';
const CONSENT_KEY =
  APP_NAME.toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_') + '_consent_v1';

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls — uses navigator.language and Intl.DateTimeFormat
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  // EU timezone prefixes
  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  // US timezone prefixes
  const usTZPrefixes = ['America/'];

  // EU country codes (ISO 3166-1 alpha-2)
  const euCountryCodes = [
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
    'GB',
    'IS',
    'LI',
    'NO',
    'CH',
  ];

  // Check EU by timezone
  const isEUByTZ = euTZPrefixes.some((p) => tz.startsWith(p));

  // Check EU by language (e.g., en-DE, fr-FR)
  const isEUByLang = euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  // Check US
  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEUByTZ || isEUByLang) return 'gdpr';
  if (isUS) return 'ccpa';

  // Thailand — PDPA consent required. Serve the full opt-in banner to the
  // product's primary market; other regions keep notice-only behavior.
  if (tz.startsWith('Asia/Bangkok') || lang.toLowerCase().startsWith('th')) return 'gdpr';

  return 'none';
}

/**
 * Get current consent record from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentRecord;
  } catch {
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function saveConsent(
  choices: ConsentRecord['choices'],
  region: ConsentRegion
): ConsentRecord {
  if (typeof window === 'undefined') throw new Error('Cannot save consent in SSR context');

  const record: ConsentRecord = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true, // Always true
      analytics: choices.analytics,
      marketing: choices.marketing,
      preferences: choices.preferences,
    },
    userAgent: navigator.userAgent,
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    console.warn('Failed to save consent to localStorage');
  }

  return record;
}

/**
 * Clear consent record from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    console.warn('Failed to clear consent from localStorage');
  }
}

/**
 * Log consent action to audit trail (for compliance tracking)
 */
export function logConsentAction(
  action: 'banner_shown' | 'accept_all' | 'reject_all' | 'preferences_saved' | 'preferences_opened',
  region: ConsentRegion,
  choices?: ConsentRecord['choices']
): void {
  if (typeof window === 'undefined') return;

  try {
    const auditKey =
      APP_NAME.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_') + '_consent_audit';
    const existing = localStorage.getItem(auditKey);
    const audit = existing ? JSON.parse(existing) : [];

    audit.push({
      timestamp: new Date().toISOString(),
      action,
      region,
      choices: choices || null,
      userAgent: navigator.userAgent,
    });

    // Keep only last 100 entries
    if (audit.length > 100) audit.shift();

    localStorage.setItem(auditKey, JSON.stringify(audit));
  } catch {
    console.warn('Failed to log consent action');
  }
}

/**
 * Check if user has consented to a specific category
 */
export function hasConsent(category: 'analytics' | 'marketing' | 'preferences'): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent.choices[category] === true;
}

/**
 * Get consent key for external use (e.g., in scripts)
 */
export function getConsentKey(): string {
  return CONSENT_KEY;
}
